import dayjs, { Dayjs } from 'dayjs';
import { PermissionClaim, PERMISSIONS } from '@/core/enums/permissions.enum';
import { ResellerAgreedPricingMode, ResellerAllocationType, ResellerCalculationType, ResellerDiscountPolicyComponent, ResellerDiscountPolicyModel, ResellerEnvironmentFeeBase, ResellerEnvironmentFeeMode, ResellerInvoiceDateType, ResellerPricingKind, SaveResellerDiscountPolicy } from '@/core/models/reseller-discount-policy.model';

export interface ResellerComponentForm {
    allocationType?: ResellerAllocationType;
    allocationValue?: number | null;
    pricingKind?: ResellerPricingKind;
    feeLadderLevelId?: string | null;
    unitTypeId?: string | null;
    pricingMode?: ResellerAgreedPricingMode;
    agreedUnitPrice?: number | null;
    calculationType?: ResellerCalculationType;
    value?: number | null;
    vatRate?: number | null;
    environmentFeeRate?: number | null;
    environmentFeeMode?: ResellerEnvironmentFeeMode;
    environmentFeeBase?: ResellerEnvironmentFeeBase;
}

export interface ResellerDraftForm {
    contractNumber: string;
    contractReferenceId?: string | null;
    invoiceDateType?: ResellerInvoiceDateType;
    effectiveFromPeriod?: Dayjs | null;
    effectiveToPeriod?: Dayjs | null;
    components?: ResellerComponentForm[];
}

export interface GoodsLevel { id: string; code?: string | null; name?: string | null; }
export interface UnitTypeChoice { id: string; name?: string | null; feeLadderId?: string | null; }

export function getResellerPolicyPermissions(claims: PermissionClaim[], userName?: string) {
    const admin = userName?.toLowerCase() === 'admin' || claims.some(claim => claim.id === PERMISSIONS.ADMIN.key);
    const has = (key: string) => admin || claims.some(claim => claim.id === key);
    return { view: has(PERMISSIONS.RESELLERDISCOUNT_VIEW.key), edit: has(PERMISSIONS.RESELLERDISCOUNT_EDIT.key), activate: has(PERMISSIONS.RESELLERDISCOUNT_ACTIVATE.key) };
}

export function validateDiscountValue(value: number | null | undefined, type?: ResellerCalculationType): string | undefined {
    if (value === null || value === undefined || !Number.isFinite(value) || value < 0) return 'Nhập mức giảm lớn hơn hoặc bằng 0.';
    if (type === 'Percent' && value > 100) return 'Mức phần trăm không được vượt quá 100.';
    if (!/^\d+(?:\.\d{1,4})?$/.test(String(value))) return 'Mức hưởng có tối đa 4 chữ số thập phân.';
    if (value > 99999999999999.9999) return 'Mức hưởng vượt quá giới hạn cho phép.';
}

export const monthlyPeriod = (value: Dayjs) => value.startOf('month').format('YYYY-MM-DD[T]00:00:00');

export const resellerPriceLabels = { AgreedBasePrice: 'Giá trước giảm, chưa VAT', AgreedFinalPrice: 'Giá cuối đã giảm, chưa VAT' };
export const resellerSourceLabels = { ...resellerPriceLabels, Components: 'Nhiều thành phần' };
export const allocationLabels = { FixedM3: 'Số m³ cố định', Percent: 'Phần trăm sản lượng đồng hồ', Remainder: 'Phần còn lại' };
export const pricingKindLabels = { AgreedPrice: 'Giá thỏa thuận', UnitTypeLadder: 'Bảng giá loại hình' };
export const resellerFeeLabels = { TariffWaterCharge: 'Tiền nước bảng giá trước chiết khấu (cơ chế cũ)', ContractWaterCharge: 'Tiền nước hợp đồng trước giảm', NetWaterCharge: 'Tiền nước sau giảm' };
export const resellerFeeModeLabels = { SellerCollects: 'Bên bán thu phí BVMT', BuyerSelfDeclares: 'Bên mua tự kê khai, nộp phí BVMT', Mixed: 'Mỗi thành phần một mức phí' };
export const resellerInvoiceDateLabels = { InMonth: 'Theo kỳ hóa đơn', ByReadDate: 'Theo ngày đọc chỉ số' };
export const formatResellerUnitPrice = (value: number) => value.toLocaleString('vi-VN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function validateAgreedUnitPrice(value: number | null | undefined): string | undefined {
    if (value == null) return 'Nhập đơn giá hợp đồng.';
    if (!Number.isFinite(value) || value <= 0) return 'Đơn giá hợp đồng phải lớn hơn 0.';
    if (!/^\d+(?:\.\d{1,2})?$/.test(String(value))) return 'Đơn giá có tối đa 2 chữ số thập phân.';
}

export function validateResellerRate(value: number | null | undefined, label: string): string | undefined {
    if (value == null) return `Nhập ${label}.`;
    if (!Number.isInteger(value) || value < 0 || value > 100) return `${label} phải là số nguyên từ 0 đến 100%.`;
}

export function blankComponent(allocationType: ResellerAllocationType = 'Remainder'): ResellerComponentForm {
    return { allocationType, pricingKind: 'AgreedPrice', pricingMode: 'AgreedBasePrice', calculationType: 'Percent', value: 0 };
}

export function matchGoodsLevel(code: string | null | undefined, levels: GoodsLevel[]) {
    const normalized = code?.trim();
    if (!normalized) return undefined;
    return levels.find(level => level.code?.trim() === normalized);
}

/** An existing one-price contract becomes one remainder slice, keeping its price and tax. */
export function componentsFromPolicy(policy: ResellerDiscountPolicyModel | null | undefined, levels: GoodsLevel[] = []): ResellerComponentForm[] {
    if (policy?.components?.length) {
        return [...policy.components].sort((left, right) => left.displayOrder - right.displayOrder).map(component => ({
            allocationType: component.allocationType,
            allocationValue: component.allocationType === 'Remainder' ? null : component.allocationValue,
            pricingKind: component.pricingKind,
            feeLadderLevelId: component.feeLadderLevelId,
            unitTypeId: component.unitTypeId,
            pricingMode: component.pricingMode ?? undefined,
            agreedUnitPrice: component.agreedUnitPrice,
            calculationType: component.calculationType ?? undefined,
            value: component.value,
            vatRate: component.vatRate,
            environmentFeeRate: component.environmentFeeRate,
            environmentFeeMode: component.environmentFeeMode ?? undefined,
            environmentFeeBase: component.environmentFeeBase ?? undefined,
        }));
    }
    if (policy?.pricingMode === 'AgreedBasePrice' || policy?.pricingMode === 'AgreedFinalPrice') {
        return [{
            allocationType: 'Remainder',
            pricingKind: 'AgreedPrice',
            feeLadderLevelId: matchGoodsLevel(policy.priceObjectCode, levels)?.id,
            pricingMode: policy.pricingMode,
            agreedUnitPrice: policy.agreedUnitPrice,
            calculationType: policy.calculationType,
            value: policy.value,
            vatRate: policy.vatRate,
            environmentFeeRate: policy.environmentFeeRate,
            environmentFeeMode: policy.environmentFeeMode ?? undefined,
            environmentFeeBase: policy.environmentFeeBase === 'TariffWaterCharge' ? undefined : policy.environmentFeeBase,
        }];
    }
    return [blankComponent()];
}

function validateComponent(component: ResellerComponentForm | undefined, index: number): string | undefined {
    const label = `Thành phần ${index + 1}`;
    if (!component?.allocationType || !allocationLabels[component.allocationType]) return `${label}: chọn cách phân bổ sản lượng.`;
    if (component.allocationType === 'FixedM3' && (!Number.isInteger(component.allocationValue) || (component.allocationValue ?? 0) <= 0)) return `${label}: nhập số m³ nguyên lớn hơn 0.`;
    if (component.allocationType === 'Percent') {
        const value = component.allocationValue;
        if (value == null || !Number.isFinite(value) || value <= 0 || value > 100 || !/^\d+(?:\.\d{1,4})?$/.test(String(value))) return `${label}: phần trăm phải lớn hơn 0, tối đa 100 và 4 số lẻ.`;
    }
    if (component.allocationType === 'Remainder' && component.allocationValue != null) return `${label}: phần còn lại không nhập số lượng.`;
    if (component.pricingKind === 'AgreedPrice') {
        if (!component.feeLadderLevelId) return `${label}: chọn bậc phí.`;
        if (component.pricingMode !== 'AgreedBasePrice' && component.pricingMode !== 'AgreedFinalPrice') return `${label}: chọn cách nhập giá.`;
        const priceError = validateAgreedUnitPrice(component.agreedUnitPrice);
        if (priceError) return `${label}: ${priceError}`;
        const calculationType = component.calculationType ?? 'Percent';
        const value = component.value ?? 0;
        const valueError = validateDiscountValue(value, calculationType);
        if (valueError) return `${label}: ${valueError}`;
        if (component.pricingMode === 'AgreedFinalPrice' && value !== 0) return `${label}: giá cuối đã giảm chỉ chấp nhận mức giảm 0.`;
        if (calculationType === 'AmountPerM3' && value > component.agreedUnitPrice!) return `${label}: giảm giá trên m³ không được vượt đơn giá.`;
        if (component.environmentFeeBase !== 'ContractWaterCharge' && component.environmentFeeBase !== 'NetWaterCharge') return `${label}: chọn cơ sở phí môi trường.`;
    } else if (component.pricingKind === 'UnitTypeLadder') {
        if (!component.unitTypeId) return `${label}: chọn loại hình đơn vị.`;
        if (component.environmentFeeBase !== 'TariffWaterCharge' && component.environmentFeeBase !== 'NetWaterCharge') return `${label}: chọn cơ sở phí môi trường.`;
    } else return `${label}: chọn cách tính tiền.`;
    const vatError = validateResellerRate(component.vatRate, 'thuế suất VAT');
    const feeError = validateResellerRate(component.environmentFeeRate, 'phí suất BVMT');
    if (vatError || feeError) return `${label}: ${vatError || feeError}`;
    if (!component.environmentFeeMode) return `${label}: chọn bên kê khai phí BVMT.`;
    if (component.environmentFeeMode === 'BuyerSelfDeclares' && component.environmentFeeRate !== 0) return `${label}: bên mua tự kê khai phải có phí suất BVMT bằng 0.`;
}

export function buildResellerDraft(waterUserId: string, form: ResellerDraftForm): SaveResellerDiscountPolicy {
    if (!waterUserId || !form.contractNumber?.trim()) throw new Error('Nhập số hợp đồng.');
    const invoiceDateType = form.invoiceDateType ?? 'InMonth';
    if (!['InMonth', 'ByReadDate'].includes(invoiceDateType)) throw new Error('Chọn cách xác định ngày hóa đơn.');
    const components = form.components ?? [];
    if (components.length < 1 || components.length > 12) throw new Error('Một hợp đồng có từ 1 đến 12 thành phần giá.');
    if (components.filter(component => component?.allocationType === 'Remainder').length !== 1) throw new Error('Phải có đúng một thành phần nhận phần còn lại.');
    const percent = components.reduce((sum, component) => sum + (component?.allocationType === 'Percent' ? component.allocationValue ?? 0 : 0), 0);
    if (percent > 100) throw new Error('Tổng phần trăm không được vượt quá 100.');
    components.forEach((component, index) => {
        const error = validateComponent(component, index);
        if (error) throw new Error(error);
    });
    if (!form.effectiveFromPeriod?.isValid()) throw new Error('Chọn kỳ bắt đầu.');
    if (form.effectiveToPeriod && (!form.effectiveToPeriod.isValid() || !form.effectiveToPeriod.startOf('month').isAfter(form.effectiveFromPeriod.startOf('month')))) throw new Error('Kỳ kết thúc phải sau kỳ bắt đầu.');
    return {
        waterUserId, contractNumber: form.contractNumber.trim(), priceObjectCode: null,
        ...(form.contractReferenceId ? { contractReferenceId: form.contractReferenceId } : {}),
        pricingMode: 'Components', agreedUnitPrice: null, vatRate: null, environmentFeeRate: null, environmentFeeMode: null,
        calculationType: 'Percent', value: 0, environmentFeeBase: 'NetWaterCharge', allowTariffDiscountStacking: false, invoiceDateType,
        effectiveFromPeriod: monthlyPeriod(form.effectiveFromPeriod),
        effectiveToPeriod: form.effectiveToPeriod ? monthlyPeriod(form.effectiveToPeriod) : null,
        components: components.map((component, index): ResellerDiscountPolicyComponent => component?.pricingKind === 'UnitTypeLadder' ? {
            displayOrder: index + 1, allocationType: component.allocationType!, allocationValue: component.allocationType === 'Remainder' ? null : component.allocationValue,
            pricingKind: 'UnitTypeLadder', unitTypeId: component.unitTypeId, feeLadderLevelId: null, pricingMode: null, agreedUnitPrice: null, calculationType: null, value: 0,
            vatRate: component.vatRate, environmentFeeRate: component.environmentFeeRate, environmentFeeMode: component.environmentFeeMode, environmentFeeBase: component.environmentFeeBase,
        } : {
            displayOrder: index + 1, allocationType: component!.allocationType!, allocationValue: component!.allocationType === 'Remainder' ? null : component!.allocationValue,
            pricingKind: 'AgreedPrice', feeLadderLevelId: component!.feeLadderLevelId, unitTypeId: null,
            pricingMode: component!.pricingMode, agreedUnitPrice: component!.agreedUnitPrice, calculationType: component!.calculationType ?? 'Percent', value: component!.value ?? 0,
            vatRate: component!.vatRate, environmentFeeRate: component!.environmentFeeRate, environmentFeeMode: component!.environmentFeeMode, environmentFeeBase: component!.environmentFeeBase,
        }),
    };
}

export function validateEndPeriod(value: Dayjs | null | undefined, policy: { effectiveFromPeriod: string; effectiveToPeriod: string | null }, now = dayjs()): string | undefined {
    if (!value?.isValid()) return 'Chọn kỳ kết thúc.';
    const month = value.startOf('month');
    if (!month.isAfter(now.startOf('month')) || !month.isAfter(dayjs(policy.effectiveFromPeriod).startOf('month'))) return 'Kỳ kết thúc phải sau tháng hiện tại và kỳ bắt đầu.';
    if (policy.effectiveToPeriod && month.isAfter(dayjs(policy.effectiveToPeriod).startOf('month'))) return 'Chỉ được rút ngắn kỳ kết thúc đã có.';
}

export function describeComponent(component: ResellerDiscountPolicyComponent, levels: GoodsLevel[], unitTypes: UnitTypeChoice[]) {
    const allocation = component.allocationType === 'FixedM3' ? `${component.allocationValue} m³`
        : component.allocationType === 'Percent' ? `${component.allocationValue}% sản lượng` : 'Phần còn lại';
    const level = levels.find(item => item.id === component.feeLadderLevelId);
    const unitType = unitTypes.find(item => item.id === component.unitTypeId);
    const price = component.pricingKind === 'UnitTypeLadder'
        ? `Bảng giá ${unitType?.name || 'loại hình'}`
        : `${level?.code?.trim() ? `${level.code.trim()} — ${level.name || level.code}` : 'Chưa chọn bậc phí'}${component.agreedUnitPrice != null ? ` · ${formatResellerUnitPrice(component.agreedUnitPrice)} đồng/m³` : ''}`;
    return `${allocation}: ${price}. VAT ${component.vatRate ?? '—'}%, phí BVMT ${component.environmentFeeRate ?? '—'}%.`;
}
